import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import IdentificationType from "../../entities/IdentificationType";
import i18n from "../../i18n";
import { apiCallBegan } from "../api";
import { AppDispatch, AppState } from "../configureStore";
import ResponseBase from "../types/ResponseBase";
import SliceCollectionBase from "../types/SliceCollectionBase";
import { authHeader } from "../users/auth";

// RESPONSE DECLARATIONS //
interface IdentificationTypeResponse extends ResponseBase {
  Code: string;
  Name: string;
}

type ClassificatorResponse<T> = T[] | { value: T[] };

// SLICE //
interface State extends SliceCollectionBase<IdentificationType> { }

const initialState: State = {
  list: [],
  loading: false,
  lastFetch: null,
  error: null
}

const slice = createSlice({
  name: "identificationTypes",
  initialState,
  reducers: {
    identificationTypesRequested: (identificationTypes) => {
      identificationTypes.loading = true;
      identificationTypes.error = null;
    },
    identificationTypesReceived: (identificationTypes, action: PayloadAction<ClassificatorResponse<IdentificationTypeResponse>>) => {
      identificationTypes.list = schema(unwrapClassificatorResponse(action.payload));
      identificationTypes.loading = false;
      identificationTypes.lastFetch = Date.now();
    },
    identificationTypesRequestFailed: (identificationTypes, action) => {
      identificationTypes.loading = false;
      identificationTypes.error = action.payload;
    }
  }
});

const {
  identificationTypesRequested,
  identificationTypesReceived,
  identificationTypesRequestFailed
} = slice.actions;

export default slice.reducer;

// ACTION CREATORS //
export const getIdentificationTypes = () => async (dispatch: AppDispatch, getState: () => AppState) => {
  if (getState().entities.identificationTypes.loading) {
    return;
  }

  const url = `/api/Classificator/identification-types`;

  return await dispatch(
    apiCallBegan({
      url,
      method: "GET",
      headers: { ...authHeader(), "Accept-Language": i18n.language },
      onStart: identificationTypesRequested.type,
      onSuccess: identificationTypesReceived.type,
      onError: identificationTypesRequestFailed.type
    })
  );
}

// NORMALIZATION //
const schema = (payload: IdentificationTypeResponse[]): IdentificationType[] => {
  if (!payload || payload.length === 0) {
    return [];
  }

  return payload.map((resp: IdentificationTypeResponse): IdentificationType => (
    {
      Code: resp.Code,
      Id: resp.Id,
      Name: resp.Name
    }
  ))
};

const unwrapClassificatorResponse = <T>(payload: ClassificatorResponse<T>): T[] => {
  if (Array.isArray(payload)) {
    return payload;
  }

  return payload?.value || [];
};
